import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useDebounce } from '@uidotdev/usehooks';
import { RequestsTable } from './RequestsTable/RequestsTable';
import { Board } from '~/components/Shared/Layout/Board/Board';
import { ErrorMessage } from '~/components/Shared/ErrorMessage/ErrorMessage';
import { Button, InputBar, Pagination } from '~/ui';
import '~/assets/css/select-table.css';
import styles from './RequestsBoard.module.scss';
import { TableSelect } from '~/ui/TableSelect/TableSelect';
import { TableAmountPage } from '~/ui/TableAmountPage/TableAmountPage';
import { useLocalStorage } from '~/utils/useLocalStorage';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import {
  checkIfUserAdmin,
  checkIfUserAssetAdmin,
  checkIfUserSuperAdmin,
} from '~/utils/getUserRole';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { getPaginate } from '~/utils/getPaginate';
import { PageTransition } from '~/components/Shared/PageTransition/PageTransition';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastWarning } from '~/utils/useToastWarning';

interface SelectedPage {
  selected: number;
}

interface Points {
  page: number;
}

interface Props {
  statusFilter?: string;
}

export const RequestsBoard = ({ statusFilter }: Props) => {
  const { initialValues, storedInitialValues, create } =
    useLocalStorage<Points>({
      initialValues: { page: 15 },
      key: 'requests-points',
      exclude: [],
    });
  const [sort, setSort] = useState({
    field: '',
    direction: '',
  });
  const [perPage, setPerPage] = useState(
    storedInitialValues?.page || initialValues.page
  );
  const { paginate } = getPaginate();
  const [page, setPage] = useState<number>(paginate);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const client = useQueryClient();
  const { t } = useTranslation();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const toastWarning = useToastWarning();
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const isAssetAdmin = checkIfUserAssetAdmin(user?.data.role_name);
  const isAdmin = checkIfUserAdmin(user?.data.role_name);
  const isDeleteAvailable = checkIfUserSuperAdmin(user?.data.role_name);

  useEffect(() => {
    create({ page: perPage });
  }, [perPage]);

  const params = {
    sort: sort.direction,
    perPage,
    field: sort.field,
    query: debouncedSearchTerm,
    page,
    ...(statusFilter ? { status_id: statusFilter } : {}),
  };

  const { data, isError, isLoading: isDataLoading, error } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/ticket', {
        params,
      });
      return response.data;
    },
    queryKey: ['requests', params],
    keepPreviousData: true,
  });

  const { mutateAsync } = useMutation(
    async () => {
      const response = await instance.post('admin/ticket-many', {
        ticket_ids: selectedItems,
        status_id: 4,
      });
      return response;
    },
    {
      onSuccess: ({ data }) => {
        const failed = data?.not_updated_tickets;

        failed?.length
          ? toastWarning(
              `${t('applications_close_failed')}: ${failed.join(', ')}`
            )
          : toastSuccess(t('applications_close_successfully'));

        setSelectedItems([]);
        client.invalidateQueries(['requests']);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredFoods = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredFoods);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleToggleAll = () => {
    if (selectedItems.length === requests?.data.length) {
      setSelectedItems([]);
    } else {
      const foodIdArray = requests?.data.map(
        (accomodation: any) => accomodation.id
      );
      setSelectedItems(foodIdArray);
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await instance.delete('admin/ticket', {
        data: { ticket_ids: selectedItems },
      });
      setSelectedItems([]);
      client.invalidateQueries(['requests']);
    } catch (error) {
      toastError(error);
    }
  };

  const requests = data?.tickets;

  return (
    <Board classNames={styles.requestsBoard}>
      <div className={styles.requestsTableOptions}>
        <TableSelect perPage={perPage} setPerPage={setPerPage} />
        <div className={styles.tableHeadRightWrapper}>
          {!!selectedItems.length &&
            (isDeleteAvailable || isAssetAdmin || isAdmin) && (
              <>
                {(isSuperAdmin || isAssetAdmin || isAdmin) && (
                  <Button
                    text={t('close')}
                    color="purple"
                    onClick={mutateAsync}
                    className={styles.deleteButton}
                  />
                )}
                {isSuperAdmin && (
                  <Button
                    text={t('delete')}
                    color="red"
                    onClick={handleDeleteSelected}
                    className={styles.deleteButton}
                  />
                )}
              </>
            )}
          <InputBar
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            value={searchTerm}
            placeholder={t('category_search')}
            classNameInput={styles.requestsTableInput}
            label={`${t('search')}:`}
            star={false}
            classNameInputWrapper={styles.requestsTableInputWrapper}
          />
        </div>
      </div>
      <PageTransition page={page}>
        {isError ? (
          <ErrorMessage error={error} />
        ) : (
          <RequestsTable
            requests={requests?.data || []}
            sort={sort}
            setSort={setSort}
            handleToggleAll={handleToggleAll}
            handleSelect={handleSelect}
            selectedItems={selectedItems}
            isLoading={isDataLoading}
          />
        )}
      </PageTransition>
      <div className={styles.requestsTablePagination}>
        <TableAmountPage
          firstRow={requests?.from}
          lastRow={requests?.to}
          total={requests?.total}
        />
        <Pagination
          forcePage={paginate}
          pageCount={requests?.last_page}
          onPageChange={(selectedPage: SelectedPage) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>
    </Board>
  );
};
